/**
 * Animated hero backdrop — a wide, soft light-gray world map (solid silhouette)
 * with subtle logistics flight-arcs radiating from the Belgium / EU hub.
 * Pure SVG + CSS (no GSAP, no framer-motion): renders to static HTML at prerender
 * time and never blocks the LCP text.
 *
 * Picfast-faithful treatment:
 *  - the gray map is ~108vw (wide, but inboard of the screen edges — not full-bleed),
 *  - the CENTER (Europe, behind the headline) is erased to page-white by `.hero-scrim`,
 *  - a soft blue wash sits between the map and the scrim,
 *  - the map fades at its bottom and CONTINUES DOWNWARD into the section below
 *    (this component is rendered in a wrapper that spans Hero + Carriers).
 *
 * Layer order back-to-front: gray map + arcs → blue wash → center scrim.
 *
 * Alignment: the map (CSS mask of /world-map.svg over a gray fill) and this arc
 * overlay share the SAME viewBox inside a box whose aspect ratio equals the map's.
 * Hub + node coordinates were verified on-land via isPointInFill against the paths.
 *
 * Map: "Simple World Map" by Al MacDonald / Fritz Lekschas — CC BY-SA 3.0.
 */

// EU hub (Belgium) in the map's viewBox coordinate space.
// Every coordinate below was verified on-land via isPointInFill against the map paths.
const HUB = { x: 416, y: 393 }

// Flight-arcs from the hub to destination nodes (quadratic Béziers, bowed upward).
// Endpoints match the first six on-land NODES below.
const ARCS = [
  'M416 393 Q294 336 172 405', // North America (California)
  'M416 393 Q353 431 290 575', // South America (Brazil)
  'M416 393 Q462 405 507 500', // East Africa (Ethiopia)
  'M416 393 Q468 391 520 465', // Middle East
  'M416 393 Q508 342 600 400', // East Asia
  'M416 393 Q547 424 677 600', // Oceania
]

// Network nodes — only the arc destinations (one dot per arc), verified on-land.
const NODES: [number, number][] = [
  [172, 405], // North America (California)
  [290, 575], // Brazil
  [507, 500], // East Africa (Ethiopia)
  [520, 465], // Middle East
  [600, 400], // East Asia
  [677, 600], // Australia
]

export default function HeroBackground() {
  return (
    <div aria-hidden className="absolute inset-0 -z-10 pointer-events-none">
      {/* Wide gray world map + arcs. Fades at its bottom (mask) into the next section. */}
      {/* Gray world-map silhouette */}
      <div className="hero-map absolute left-1/2 top-0 -translate-x-1/2 w-[95vw] lg:w-[92vw] max-w-none aspect-[784/459]">
        <div className="hero-map-fill absolute inset-0" />
      </div>

      {/* Blue wash the user likes — sits above the gray map */}
      <div className="absolute inset-x-0 -top-24 h-[760px] bg-hero-wash opacity-60" />

      {/* Center scrim — cleans the gray map behind the headline. The arcs render
          ABOVE it (see below) so their dark-blue trace stays visible from the hub. */}
      <div className="hero-scrim absolute inset-x-0 top-0 h-[780px]" />

      {/* Logistics arcs + nodes + hub — rendered ABOVE the scrim so the dark-blue
          trace is visible all the way from the Belgium hub to each destination,
          while the scrim still keeps the gray map clean behind the headline text. */}
      <div className="hero-map absolute left-1/2 top-0 -translate-x-1/2 w-[95vw] lg:w-[92vw] max-w-none aspect-[784/459]">
        <svg
          className="absolute inset-0 h-full w-full"
          viewBox="30.767 241.591 784.077 458.627"
          fill="none"
          preserveAspectRatio="xMidYMid meet"
        >
          {/* Per-arc gradient: transparent at the Belgium hub → really dark blue at
              the destination, so the trace stays light at the start (won't mask the
              text) and darkens only as it reaches the country. */}
          <defs>
            {ARCS.map((_, i) => {
              const [dx, dy] = NODES[i]
              return (
                <linearGradient
                  key={`g-${i}`}
                  id={`heroArc${i}`}
                  gradientUnits="userSpaceOnUse"
                  x1={HUB.x}
                  y1={HUB.y}
                  x2={dx}
                  y2={dy}
                >
                  <stop offset="0%" stopColor="#3b5dff" stopOpacity="0" />
                  <stop offset="50%" stopColor="#2a3ff5" stopOpacity="0.06" />
                  <stop offset="78%" stopColor="#242fd6" stopOpacity="0.28" />
                  <stop offset="100%" stopColor="#1b2480" stopOpacity="0.95" />
                </linearGradient>
              )
            })}
          </defs>

          {/* Faint always-on route hint (same gradient, low opacity). The id is
              the geometry reference for the parcels' animateMotion below. */}
          {ARCS.map((d, i) => (
            <path
              key={`base-${i}`}
              id={`heroArcPath${i}`}
              d={d}
              className="hero-arc-base"
              stroke={`url(#heroArc${i})`}
              opacity={0.4}
            />
          ))}

          {/* Thin trace that draws itself from the hub to each destination,
              staggered so the network builds sequentially */}
          {ARCS.map((d, i) => (
            <path
              key={`flow-${i}`}
              d={d}
              pathLength={100}
              className="hero-arc-flow"
              stroke={`url(#heroArc${i})`}
              style={{
                animationDuration: `${7.5 + (i % 3) * 0.7}s`,
                animationDelay: `${-i * 1.5}s`,
              }}
            />
          ))}

          {/* Destination nodes */}
          {NODES.map(([cx, cy], i) => (
            <circle
              key={`node-${i}`}
              cx={cx}
              cy={cy}
              r={2.1}
              className="hero-node"
              style={{ animationDelay: `${-i * 0.6}s` }}
            />
          ))}

          {/* Colis — un petit paquet voyage le long de chaque arc, du hub belge
              vers sa destination. SMIL natif (animateMotion + mpath) : zéro JS,
              présent dès le HTML prérendu, masqué sous prefers-reduced-motion.
              L'opacité ne monte qu'à partir de ~40 % du trajet : près du hub les
              arcs sont transparents (le centre reste propre pour le texte), le
              colis n'apparaît que là où sa route est visible. */}
          {ARCS.map((_, i) => {
            const dur = 7 + (i % 3) * 1.3
            const begin = `${-(i * 2.3)}s`
            return (
              <g key={`parcel-${i}`} className="hero-parcel">
                <rect x="-1.7" y="-1.2" width="3.4" height="2.4" rx="0.55" fill="#1b2480">
                  <animateMotion dur={`${dur}s`} begin={begin} repeatCount="indefinite" rotate="auto" calcMode="linear">
                    <mpath href={`#heroArcPath${i}`} />
                  </animateMotion>
                  <animate
                    attributeName="opacity"
                    values="0;0;0.9;0.9;0"
                    keyTimes="0;0.38;0.55;0.85;1"
                    dur={`${dur}s`}
                    begin={begin}
                    repeatCount="indefinite"
                  />
                </rect>
              </g>
            )
          })}

        </svg>
      </div>
    </div>
  )
}
